import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Sparkles, FileText, Loader2, Download, Brain, 
  Target, Beaker, TrendingUp, ArrowRight, AlertTriangle, FileDown, RefreshCw
} from 'lucide-react';
import { paperApi } from '../services/api';
import { Button } from './ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { ScrollArea } from './ui/scroll-area';
import { toast } from 'sonner';
import GenZPanel from './analysis/GenZPanel';
import TechnicalDeepDive from './analysis/TechnicalDeepDive';
import ImpactScorecard from './analysis/ImpactScorecard';
import InteractiveRoadmap from './analysis/InteractiveRoadmap';
import FlowChart from './analysis/FlowChart';
import AskThePaper from './analysis/AskThePaper';
import PaperSummary from './analysis/PaperSummary';

const AnalysisView = ({ paper, onAnalysisComplete, onAnalysisError, isUploading }) => {
  const [analyzing, setAnalyzing] = useState(false);
  const [activeTab, setActiveTab] = useState('summary');
  const [exporting, setExporting] = useState(false);
  
  const isLoading = analyzing || isUploading;

  const handleAnalyze = async (isRetry = false) => {
    setAnalyzing(true);
    try {
      const response = await paperApi.analyze(paper.id);
      const updatedPaper = {
        ...paper,
        analyzed: true,
        analysis: response.data.analysis
      };
      onAnalysisComplete(updatedPaper);
      
      if (response.data.analysis?.analysis_issues) {
        toast.warning('Some sections could not be fully analyzed. See details below.');
      } else {
        toast.success('Analysis complete!');
      }
      setAnalyzing(false);
    } catch (error) {
      if (!isRetry) {
        toast.info('Taking a moment, retrying...');
        setTimeout(() => handleAnalyze(true), 4000);
      } else {
        if (onAnalysisError) onAnalysisError(error);
        toast.error(error.response?.data?.detail || 'Analysis failed');
        setAnalyzing(false);
      }
    }
  };

  const handleDownload = async () => {
    try {
      const response = await paperApi.download(paper.id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', paper.original_filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download paper');
    }
  };

  const handleExportPDF = async () => {
    if (!paper.analyzed) {
      toast.error('Please analyze the paper first');
      return;
    }
    
    setExporting(true);
    try {
      const response = await paperApi.exportPDF(paper.id);
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      const safeTitle = paper.title.replace(/[^a-zA-Z0-9\s-_]/g, '').slice(0, 50);
      link.setAttribute('download', `Simplify_Analysis_${safeTitle}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Analysis exported as PDF!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to export PDF');
    } finally {
      setExporting(false);
    }
  };

  const analysis = paper.analysis;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="h-full flex flex-col"
      data-testid="analysis-view"
    >
      {/* Header */}
      <div className={`px-4 lg:px-6 py-3 lg:py-4 border-b border-slate-700/50 bg-slate-800/30 ${isLoading ? 'hidden' : ''}`}>
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
          <div className="flex-1 min-w-0 pr-0 sm:pr-4">
            <h1 className="text-lg lg:text-xl font-heading font-bold text-white truncate">
              {paper.title}
            </h1>
            <p className="text-xs lg:text-sm text-slate-400 mt-1">
              {paper.original_filename} • {(paper.size / 1024).toFixed(1)} KB
            </p>
          </div>
          <div className="flex items-center gap-2 lg:gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownload}
              data-testid="download-paper-button"
              className="border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white text-xs lg:text-sm"
            >
              <Download className="w-3 h-3 lg:w-4 lg:h-4 mr-1 lg:mr-2" />
              <span className="hidden sm:inline">Original</span>
            </Button>
            {paper.analyzed && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportPDF}
                disabled={exporting}
                data-testid="export-pdf-button"
                className="border-teal-500/50 text-teal-400 hover:bg-teal-500/10 hover:text-teal-300 text-xs lg:text-sm"
              >
                {exporting ? (
                  <Loader2 className="w-3 h-3 lg:w-4 lg:h-4 animate-spin" />
                ) : (
                  <>
                    <FileDown className="w-3 h-3 lg:w-4 lg:h-4 mr-1 lg:mr-2" />
                    <span className="hidden sm:inline">Export PDF</span>
                  </>
                )}
              </Button>
            )}
            {!paper.analyzed && !isLoading && (
              <Button
                onClick={handleAnalyze}
                disabled={analyzing}
                size="sm"
                data-testid="analyze-paper-button"
                className="bg-teal-500 hover:bg-teal-400 text-slate-900 font-semibold text-xs lg:text-sm"
              >
                {analyzing ? (
                  <>
                    <Loader2 className="w-3 h-3 lg:w-4 lg:h-4 mr-1 lg:mr-2 animate-spin" />
                    <span className="hidden sm:inline">Analyzing...</span>
                    <span className="sm:hidden">...</span>
                  </>
                ) : (
                  <>
                    <Sparkles className="w-3 h-3 lg:w-4 lg:h-4 mr-1 lg:mr-2" />
                    Analyze
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      {!paper.analyzed ? (
        <div className="flex-1 flex items-start justify-center p-4 pt-12">
          {isLoading ? (
            <div className="text-center">
              <div className="w-16 h-16 lg:w-20 lg:h-20 mx-auto mb-4 lg:mb-6 rounded-full bg-teal-400/10 flex items-center justify-center animate-pulse-glow">
                <Brain className="w-8 h-8 lg:w-10 lg:h-10 text-teal-400 animate-pulse" />
              </div>
              <h3 className="text-lg lg:text-xl font-heading font-semibold text-white mb-2">
                {isUploading ? 'Uploading & Analyzing...' : 'Analyzing Paper'}
              </h3>
              <p className="text-sm text-slate-400 max-w-sm">
                {isUploading 
                  ? 'Your paper is being uploaded and will be analyzed automatically...'
                  : 'Our AI is reading through your research paper and extracting key insights...'}
              </p>
              <div className="mt-4 lg:mt-6 flex flex-wrap items-center justify-center gap-2 lg:gap-3">
                {['Extracting', 'Analyzing', 'Generating'].map((step, i) => (
                  <div key={step} className="flex items-center gap-1 lg:gap-2 text-xs lg:text-sm text-slate-500">
                    <div className={`w-2 h-2 rounded-full ${i === 1 ? 'bg-teal-400 animate-pulse' : 'bg-slate-600'}`} />
                    {step}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center max-w-md px-4 lg:px-6">
              <div className="w-16 h-16 lg:w-20 lg:h-20 mx-auto mb-4 lg:mb-6 rounded-2xl lg:rounded-3xl bg-slate-800 flex items-center justify-center">
                <FileText className="w-8 h-8 lg:w-10 lg:h-10 text-slate-500" />
              </div>
              <h3 className="text-lg lg:text-xl font-heading font-semibold text-white mb-2">
                Paper Not Analyzed
              </h3>
              <p className="text-sm text-slate-400 mb-4 lg:mb-6">
                Click "Analyze" to get AI-powered insights, summaries, and breakdowns.
              </p>
              <Button
                onClick={handleAnalyze}
                data-testid="analyze-paper-cta"
                className="bg-teal-500 hover:bg-teal-400 text-slate-900 font-semibold"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Start Analysis
              </Button>
            </div>
          )}
        </div>
      ) : (
        <div className="flex-1 overflow-hidden">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full flex flex-col">

            {/* Analysis Issues Warning */}
            {analysis?.analysis_issues && (
              <div className="mx-4 lg:mx-6 mt-3 p-3 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-amber-400">Partial Analysis</p>
                    <p className="text-xs text-slate-400 mt-1">{analysis.analysis_issues}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => setActiveTab('ask')}
                    className="text-xs px-2 py-1 rounded-lg bg-teal-500/10 text-teal-400 hover:bg-teal-500/20 border border-teal-500/30 flex items-center gap-1"
                  >
                    <Sparkles className="w-3 h-3" />
                    Ask
                  </button>
                  <Button
                    onClick={handleAnalyze}
                    disabled={analyzing}
                    size="sm"
                    data-testid="reanalyze-button"
                    className="bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 border border-amber-500/30"
                  >
                    {analyzing ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <RefreshCw className="w-3 h-3 mr-1" />
                        Retry
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}

            {/* Tab Navigation */}
            <div className="px-4 lg:px-6 pt-3 lg:pt-4 border-b border-slate-700/50 overflow-x-auto">
              <TabsList className="bg-slate-800/50 p-1 rounded-xl inline-flex min-w-max">
                <TabsTrigger 
                  value="summary" 
                  data-testid="tab-summary"
                  className="rounded-lg data-[state=active]:bg-teal-500 data-[state=active]:text-slate-900 text-xs lg:text-sm px-2 lg:px-3"
                >
                  <FileText className="w-3 h-3 lg:w-4 lg:h-4 mr-1 lg:mr-2" />
                  Summary
                </TabsTrigger>
                <TabsTrigger 
                  value="technical"
                  data-testid="tab-technical"
                  className="rounded-lg data-[state=active]:bg-teal-500 data-[state=active]:text-slate-900 text-xs lg:text-sm px-2 lg:px-3"
                >
                  <Beaker className="w-3 h-3 lg:w-4 lg:h-4 mr-1 lg:mr-2" />
                  Technical
                </TabsTrigger>
                <TabsTrigger 
                  value="impact"
                  data-testid="tab-impact"
                  className="rounded-lg data-[state=active]:bg-teal-500 data-[state=active]:text-slate-900 text-xs lg:text-sm px-2 lg:px-3"
                >
                  <TrendingUp className="w-3 h-3 lg:w-4 lg:h-4 mr-1 lg:mr-2" />
                  Impact
                </TabsTrigger>
                <TabsTrigger 
                  value="roadmap"
                  data-testid="tab-roadmap"
                  className="rounded-lg data-[state=active]:bg-teal-500 data-[state=active]:text-slate-900 text-xs lg:text-sm px-2 lg:px-3"
                >
                  <Target className="w-3 h-3 lg:w-4 lg:h-4 mr-1 lg:mr-2" />
                  Roadmap
                </TabsTrigger>
                <TabsTrigger 
                  value="flowchart"
                  data-testid="tab-flowchart"
                  className="rounded-lg data-[state=active]:bg-teal-500 data-[state=active]:text-slate-900 text-xs lg:text-sm px-2 lg:px-3"
                >
                  <ArrowRight className="w-3 h-3 lg:w-4 lg:h-4 mr-1 lg:mr-2" />
                  Flow
                </TabsTrigger>
                <TabsTrigger 
                  value="ask"
                  data-testid="tab-ask"
                  className="rounded-lg data-[state=active]:bg-teal-500 data-[state=active]:text-slate-900 text-xs lg:text-sm px-2 lg:px-3"
                >
                  <Sparkles className="w-3 h-3 lg:w-4 lg:h-4 mr-1 lg:mr-2" />
                  Ask
                </TabsTrigger>
              </TabsList>
            </div>

            {/* Tab Content */}
            <ScrollArea className="flex-1">
              <div className="p-4 lg:p-6">
                <TabsContent value="summary" className="m-0">
                  <div className="flex flex-col lg:flex-row gap-4 lg:gap-6">
                    <div className="flex-1">
                      <PaperSummary summary={analysis?.summary} />
                    </div>
                    <GenZPanel eli15={analysis?.eli15} />
                  </div>
                </TabsContent>
                
                <TabsContent value="technical" className="m-0">
                  <TechnicalDeepDive technical={analysis?.technical} />
                </TabsContent>
                
                <TabsContent value="impact" className="m-0">
                  <ImpactScorecard impact={analysis?.impact} />
                </TabsContent>
                
                <TabsContent value="roadmap" className="m-0">
                  <InteractiveRoadmap roadmap={analysis?.roadmap} />
                </TabsContent>
                
                <TabsContent value="flowchart" className="m-0">
                  <FlowChart flowchart={analysis?.flowchart} />
                </TabsContent>

                <TabsContent value="ask" className="m-0">
                  <AskThePaper
                    paperTitle={paper.title}
                    paperText={analysis?.extracted_text || ''}
                    questionsUsed={paper.questionsUsed || 0}
                    onQuestionAsked={() => {}}
                  />
                </TabsContent>
              </div>
            </ScrollArea>
          </Tabs>
        </div>
      )}
    </motion.div>
  );
};

export default AnalysisView;